import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Check as CheckIcon,
  KeyboardArrowDown as ChevronIcon,
} from '@mui/icons-material';
import { SolutionStory } from '../../services/api';
import {
  buildImpactSummary,
  resolveStoryStage,
  splitCsv,
} from './storyHelpers';
import { STAGE_BADGE_SX, STAGE_LABEL, STORY_UI } from './storyUiTokens';

export interface StoryCardProps {
  story: SolutionStory;
  selected?: boolean;
  /** Called with the story id, so the parent can pass one stable function for the whole list. */
  onToggleSelect?: (id: string) => void;
  onCardClick: (id: string) => void;
  /** When false, hide the selection checkbox (admin lists). Default true. */
  showSelect?: boolean;
}

const StoryCardComponent: React.FC<StoryCardProps> = ({
  story,
  selected = false,
  onToggleSelect,
  onCardClick,
  showSelect = true,
}) => {
  const impactSummary = buildImpactSummary(story);
  const stage = resolveStoryStage(story);
  const stageStyle = STAGE_BADGE_SX[stage];
  const techTags = splitCsv(story.tech_stack);
  const [tagsExpanded, setTagsExpanded] = useState(false);

  const colorChipSx = (hex: string) => ({
    fontSize: '11.5px',
    color: hex,
    backgroundColor: `${hex}1A`,
    px: '10px',
    py: '4px',
    borderRadius: STORY_UI.radiusPill,
    fontWeight: 600,
  });

  type Chip = { key: string; label: string; sx: object };

  const chips: Chip[] = [
    ...(story.industry
      ? [{ key: 'industry', label: story.industry, sx: colorChipSx('#734BFF') }]
      : []),
    ...(story.region ? [{ key: 'region', label: story.region, sx: colorChipSx('#F4B643') }] : []),
    ...(story.platform ? splitCsv(story.platform) : []).map((p) => ({
      key: `platform-${p}`,
      label: p,
      sx: {
        fontSize: '11.5px',
        color: STORY_UI.inkSoft,
        backgroundColor: STORY_UI.surfaceSunken,
        px: '10px',
        py: '4px',
        borderRadius: STORY_UI.radiusPill,
        fontWeight: 500,
      },
    })),
    ...techTags.map((t) => ({
      key: `tech-${t}`,
      label: t,
      sx: {
        fontSize: '11.5px',
        color: STORY_UI.accentDeep,
        backgroundColor: STORY_UI.accentTint,
        px: '10px',
        py: '4px',
        borderRadius: STORY_UI.radiusPill,
        fontWeight: 500,
        fontFamily: STORY_UI.fontMono,
      },
    })),
    ...(story.review_status && story.review_status !== 'Approved'
      ? [
          {
            key: 'review',
            label: story.review_status,
            sx: {
              fontSize: '11.5px',
              color: story.review_status === 'Pending' ? '#92400e' : STORY_UI.accentDeep,
              backgroundColor: story.review_status === 'Pending' ? '#fef3c7' : STORY_UI.accentTint,
              px: '10px',
              py: '4px',
              borderRadius: STORY_UI.radiusPill,
              fontWeight: 600,
            },
          },
        ]
      : []),
  ];

  const MOBILE_VISIBLE_CHIPS = 3;
  const mobileHiddenCount = chips.length - MOBILE_VISIBLE_CHIPS;

  const checkbox = (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onToggleSelect?.(story.solution_story_id);
      }}
      sx={{
        width: 20,
        height: 20,
        borderRadius: '6px',
        border: `1.5px solid ${selected ? STORY_UI.accent : STORY_UI.lineStrong}`,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: selected ? STORY_UI.accent : '#fff',
        transition: 'all .15s ease',
      }}
    >
      <CheckIcon
        sx={{
          fontSize: 12,
          color: '#fff',
          opacity: selected ? 1 : 0,
          transition: 'opacity .1s ease',
        }}
      />
    </Box>
  );

  const stageBadge = (
    <Box
      component="span"
      sx={{
        fontSize: '11px',
        fontWeight: 700,
        px: '13px',
        py: '6px',
        borderRadius: STORY_UI.radiusPill,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
        backgroundColor: stageStyle.bg,
        color: stageStyle.color,
      }}
    >
      {STAGE_LABEL[stage]}
    </Box>
  );

  return (
    <Box
      onClick={() => onCardClick(story.solution_story_id)}
      sx={{
        position: 'relative',
        backgroundColor: STORY_UI.surface,
        border: `1px solid ${STORY_UI.line}`,
        borderRadius: STORY_UI.radiusLg,
        boxShadow: STORY_UI.shadowCard,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: '12px', sm: '18px' },
        px: { xs: '16px', sm: '26px' },
        py: { xs: '14px', sm: '24px' },
        cursor: 'pointer',
        fontFamily: STORY_UI.fontSans,
        transition: 'border-color .15s ease, transform .1s ease',
        '&:hover': {
          borderColor: STORY_UI.lineStrong,
          transform: 'translateY(-1px)',
        },
      }}
    >
      {/* Desktop-only: company name pinned to the card's top-right corner */}
      {story.account_name && (
        <Typography
          sx={{
            display: { xs: 'none', sm: 'block' },
            position: 'absolute',
            top: '14px',
            right: '26px',
            fontWeight: 700,
            fontSize: '12.5px',
            color: '#0064FF',
          }}
        >
          {story.account_name}
        </Typography>
      )}

      {/* Mobile-only header row: checkbox + stage badge, so neither steals width from the title below */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          alignItems: 'center',
          justifyContent: showSelect ? 'space-between' : 'flex-end',
        }}
      >
        {showSelect && checkbox}
        {stageBadge}
      </Box>

      {showSelect && <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>{checkbox}</Box>}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {(story.solution_story_id || story.pr_code) && (
          <Typography
            sx={{
              fontFamily: STORY_UI.fontMono,
              fontSize: '11px',
              fontWeight: 500,
              color: STORY_UI.accentDeep,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: '4px',
            }}
          >
            {[story.solution_story_id, story.pr_code].filter(Boolean).join(' · ')}
          </Typography>
        )}
        <Typography
          sx={{
            fontFamily: STORY_UI.fontDisplay,
            fontWeight: 600,
            fontSize: { xs: '15.5px', sm: '18px' },
            color: STORY_UI.ink,
            letterSpacing: '-0.01em',
            mb: '6px',
            lineHeight: 1.3,
          }}
        >
          {story.solution_provided_title}
        </Typography>
        {story.is_deal_win_only && story.winning_strategy_subheading && (
          <Typography
            sx={{
              fontSize: '13px',
              color: STORY_UI.inkSoft,
              mb: '8px',
              lineHeight: 1.4,
            }}
          >
            {story.winning_strategy_subheading}
          </Typography>
        )}
        {story.account_name && (
          <Typography
            sx={{
              display: { xs: 'block', sm: 'none' },
              fontWeight: 700,
              fontSize: '13px',
              color: '#0064FF',
              mb: '8px',
            }}
          >
            {story.account_name}
          </Typography>
        )}
        {impactSummary && (
          <Typography
            sx={{
              fontSize: '13.5px',
              color: STORY_UI.inkSoft,
              mb: '13px',
              lineHeight: 1.45,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: { xs: 2, sm: 3 },
              overflow: 'hidden',
            }}
          >
            {impactSummary}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
          {/* Desktop: always show every tag */}
          {chips.map((chip) => (
            <Box key={chip.key} component="span" sx={{ display: { xs: 'none', sm: 'inline-block' }, ...chip.sx }}>
              {chip.label}
            </Box>
          ))}

          {/* Mobile: collapsed to a few tags by default, with a toggle to reveal the rest */}
          {(tagsExpanded ? chips : chips.slice(0, MOBILE_VISIBLE_CHIPS)).map((chip) => (
            <Box key={chip.key} component="span" sx={{ display: { xs: 'inline-block', sm: 'none' }, ...chip.sx }}>
              {chip.label}
            </Box>
          ))}
          {mobileHiddenCount > 0 && (
            <Box
              component="button"
              onClick={(e) => {
                e.stopPropagation();
                setTagsExpanded((prev) => !prev);
              }}
              sx={{
                display: { xs: 'inline-block', sm: 'none' },
                fontSize: '11.5px',
                color: STORY_UI.accent,
                backgroundColor: 'transparent',
                border: `1px solid ${STORY_UI.line}`,
                px: '10px',
                py: '4px',
                borderRadius: STORY_UI.radiusPill,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: STORY_UI.fontSans,
              }}
            >
              {tagsExpanded ? 'Show less' : `+${mobileHiddenCount} more`}
            </Box>
          )}
        </Box>
      </Box>

      {/* Mobile-only footer row: expand chevron, separated from content above */}
      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          alignItems: 'center',
          justifyContent: 'flex-end',
          pt: '10px',
          mt: '2px',
          borderTop: `1px solid ${STORY_UI.line}`,
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            backgroundColor: STORY_UI.surfaceSunken,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: STORY_UI.inkSoft,
          }}
        >
          <ChevronIcon sx={{ fontSize: 16, transform: 'rotate(-90deg)' }} />
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          gap: '18px',
          flexShrink: 0,
        }}
      >
        {stageBadge}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: STORY_UI.surfaceSunken,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: STORY_UI.inkSoft,
            flexShrink: 0,
          }}
        >
          <ChevronIcon sx={{ fontSize: 16, transform: 'rotate(-90deg)' }} />
        </Box>
      </Box>
    </Box>
  );
};

/** Memoized: with stable onToggleSelect/onCardClick refs from the parent, typing in an
 * unrelated search box won't re-render every card in a list of hundreds. */
export const StoryCard = React.memo(StoryCardComponent);

export default StoryCard;
